import { getLatestStatusEntry, sortStatusHistoryChronologically } from './statusRemarks';

/** Analytics-only: map legacy pending labels to schedule for present-interview checks (not used on save). */
export const LEGACY_STATUS_LABEL_MAP = {
  'L1 Pending': 'L1 Schedule',
  'L2 Pending': 'L2 Schedule',
};

export const STATUSES_REQUIRING_INTERVIEW_DATE = new Set([
  'L1 Schedule',
  'L2 Schedule',
  'L3 Schedule',
]);

export const STATUSES_WITHOUT_INTERVIEW_DATE = new Set([
  'L1 Pending',
  'L2 Pending',
  'L3 Pending',
  'L1 To be Schedule',
  'L2 To be Schedule',
  'L3 To be Schedule',
]);

export const STATUS_COLORS = {
  green: '#16A34A',
  orange: '#510bf5',
  red: '#DC2626',
  blue: '#2563EB',
};

export const STATUS_DISPLAY_LABELS = {
  'Ornnova Screen Selected': 'ORNNOVA Screening',
  'ORNNOVA Screening Reject': 'Ornnova Screen Reject',
  'Shared with Client': 'Profile Shared',
  'Shared to Portal': 'Shared to Portal',
  'Uploaded in Portal': 'Uploaded in Portal',
  'Duplicate Profile': 'Duplicate Profile',
  'Client Rejected': 'Client Rejected',
  'Internal Reject': 'Internal Reject',
  'L1 Schedule': 'Interview Scheduled',
  'L1 Pending': 'Interview Scheduled',
  'L1 Selected': 'Interview Completed',
  'L1 Done': 'Interview Completed',
  'L1 Rejected': 'Interview Rejected',
  'L1 Postponed': 'Interview Scheduled',
  'L1 Hold': 'Interview Scheduled',
  'L1 Not Attended': 'Interview Scheduled',
  'L2 Schedule': 'Interview Scheduled',
  'L2 Pending': 'Interview Scheduled',
  'L2 Selected': 'Interview Completed',
  'L2 Done': 'Interview Completed',
  'L2 Rejected': 'Interview Rejected',
  'L2 Postponed': 'Interview Scheduled',
  'L2 Hold': 'Interview Scheduled',
  'L2 Not Attended': 'Interview Scheduled',
  'L3 Schedule': 'Interview Scheduled',
  'L3 Selected': 'HR Round',
  'L3 Done': 'HR Round',
  'L3 Rejected': 'Interview Rejected',
  'L3 Hold': 'HR Round',
  'L3 Postponed': 'HR Round',
  'L3 Not Attended': 'HR Round',
  'Offer Released': 'Offer Released',
  'Offer Accepted': 'Offer Accepted',
  'Onboard Confirmation': 'Successfully Closed',
  'Joined': 'Joined',
  'Rejected': 'Internal Reject',
  'Candidate Declined': 'Offer Reject',
  'No Action Taken': 'No Action Taken',
};

const STATUS_SUCCESS = new Set([
  'Joined',
  'Offer Accepted',
  'Onboard Confirmation',
]);

const SYSTEM_BADGE_STATUSES = new Set([
  'Active',
  'InActive',
  'Inactive',
  'high',
  'medium',
  'low',
  'N/A',
]);

const STATUS_DROPDOWN_LABELS = {
  'ORNNOVA Screening Reject': 'Ornnova Screen Reject',
};

/** Dropdown display labels — stored values stay unchanged. */
export function getStatusDropdownLabel(status) {
  if (!status) return 'Select Status';
  return STATUS_DROPDOWN_LABELS[status] || status;
}

export const ALL_STATUS_OPTIONS = [
  'Ornnova Screen Selected',
  'Ornnova Review',
  'Back up Profile',
  'ORNNOVA Screening Reject',
  'Shared with Client',
  'Shared to Portal',
  'Uploaded in Portal',
  'Duplicate Profile',
  'Client Rejected',
  'Internal Reject',
  'L1 Schedule',
  'L1 Pending',
  'L1 To be Schedule',
  'L1 Selected',
  'L1 Done',
  'L1 Rejected',
  'L1 Postponed',
  'L1 Hold',
  'L1 Not Attended',
  'L2 Schedule',
  'L2 Pending',
  'L2 To be Schedule',
  'L2 Selected',
  'L2 Done',
  'L2 Rejected',
  'L2 Postponed',
  'L2 Hold',
  'L2 Not Attended',
  'L3 Schedule',
  'L3 Pending',
  'L3 To be Schedule',
  'L3 Selected',
  'L3 Done',
  'L3 Rejected',
  'L3 Hold',
  'L3 Postponed',
  'L3 Not Attended',
  'HR Round',
  'Offer to be released',
  'Offer Released',
  'Offer Accepted',
  'Onboard Confirmation',
  'Joined',
  'Rejected',
  'Candidate Declined',
];

export const REJECTED_STATUSES = new Set([
  'Rejected',
  'Declined',
  'Candidate Declined',
  'Client Rejected',
  'L1 Rejected',
  'L2 Rejected',
  'L3 Rejected',
  'ORNNOVA Screening Reject',
  'Internal Reject',
  'Duplicate Profile',
]);

/** Admin Dashboard → Present Interviews: interview / HR-round stage only (canonical stored values). */
export const PRESENT_INTERVIEW_STATUSES = new Set([
  'Selected',
  'L1 Schedule',
  'L2 Schedule',
  'L3 Schedule',
  'L1 Selected',
  'L2 Selected',
  'L3 Selected',
  'L1 Done',
  'L2 Done',
  'L3 Done',
  'L1 Hold',
  'L2 Hold',
  'L3 Hold',
  'L1 Postponed',
  'L2 Postponed',
  'L3 Postponed',
  'L1 Not Attended',
  'L2 Not Attended',
  'L3 Not Attended',
  'HR Round',
]);

/** Latest status in these values excludes Present Interviews (offer / onboarding / terminal). */
export const PRESENT_INTERVIEW_EXCLUDED_STATUSES = new Set([
  'Offer to be released',
  'Offer Released',
  'Offer Accepted',
  'Onboard Confirmation',
  'Joined',
  'Candidate Declined',
]);

export function isPresentInterviewStatus(status) {
  if (!status) return false;
  const normalized = LEGACY_STATUS_LABEL_MAP[status] || status;
  if (PRESENT_INTERVIEW_EXCLUDED_STATUSES.has(status)
    || PRESENT_INTERVIEW_EXCLUDED_STATUSES.has(normalized)) {
    return false;
  }
  if (STATUSES_WITHOUT_INTERVIEW_DATE.has(status)) return true;
  return PRESENT_INTERVIEW_STATUSES.has(normalized);
}

export function isPresentInterviewCandidate(candidate) {
  return isPresentInterviewStatus(getLatestCandidateStatus(candidate));
}

export function getStatusDisplayLabel(status) {
  if (!status) return 'No Action Taken';
  return STATUS_DISPLAY_LABELS[status] || LEGACY_STATUS_LABEL_MAP[status] || getStatusDropdownLabel(status);
}

/** UI-only color category: blue | orange | red | green */
export function getStatusColorCategory(status) {
  const raw = !status || status === '' ? 'No Action Taken' : status;
  if (raw === 'No Action Taken') return 'blue';
  if (REJECTED_STATUSES.has(raw)) return 'red';
  if (STATUS_SUCCESS.has(raw)) return 'green';
  return 'orange';
}

export function getStatusColor(status) {
  return STATUS_COLORS[getStatusColorCategory(status)];
}

export function getStatusTone(status) {
  return getStatusColorCategory(status);
}

export function isSourceProfileStatus(status) {
  if (!status || status === 'No Action Taken') return true;
  return ALL_STATUS_OPTIONS.includes(status);
}

export function isSystemBadgeStatus(status) {
  return SYSTEM_BADGE_STATUSES.has(status);
}

/** Full Source Profile Details edit (all fields) — allowed only before first status action. */
export function canEditFullSourceProfileDetails(candidate) {
  return getRecentCandidateStatus(candidate) === 'No Action Taken';
}

export function getRecentCandidateStatus(candidate) {
  if (candidate?.currentStatus) return candidate.currentStatus;

  const latestEntry = getLatestStatusEntry(candidate?.Status);
  if (!latestEntry?.Status) return 'No Action Taken';
  return latestEntry.Status;
}

function getLatestStoredStatusFromHistory(candidate) {
  const history = Array.isArray(candidate?.Status) ? candidate.Status : [];
  if (history.length === 0) return '';

  const sortedHistory = sortStatusHistoryChronologically(history);
  const latestEntry = getLatestStatusEntry(sortedHistory);
  return String(latestEntry?.Status || latestEntry?.status || '').trim();
}

/** Persisted profile status from API/DB (no display-label mapping). */
export function getPersistedCandidateStatus(candidate) {
  const fromHistory = getLatestStoredStatusFromHistory(candidate);
  if (fromHistory) return fromHistory;

  const fromApi = String(candidate?.currentStatus || '').trim();
  if (fromApi) return fromApi;

  return 'No Action Taken';
}

export const STATUS_TRANSITIONS = ALL_STATUS_OPTIONS.reduce((acc, status) => {
  acc[status] = ALL_STATUS_OPTIONS.filter((option) => option !== status);
  return acc;
}, {});

export function displayCandidateStatus(status) {
  return getStatusDisplayLabel(status);
}

/** Returns the status unchanged (no pending → schedule conversion). */
export function normalizeCandidateStatus(status) {
  return status;
}

export const getLatestCandidateStatus = (candidate) => {
  if (candidate?.currentStatus) return candidate.currentStatus;

  const latestEntry = getLatestStatusEntry(candidate?.Status);
  if (!latestEntry?.Status) return '';
  return latestEntry.Status;
};

export const getLatestCandidateStatusRaw = (candidate) => {
  if (candidate?.currentStatus) return candidate.currentStatus;

  const latestEntry = getLatestStatusEntry(candidate?.Status);
  if (!latestEntry?.Status) return 'No Action Taken';
  return latestEntry.Status;
};

export function shouldShowInterviewDateForStatusEntry(statusEntry, candidate) {
  if (!candidate?.interviewDate) return false;
  if (!isInterviewScheduleStatus(statusEntry?.Status)) return false;

  const latestStatusEntryId = candidate?.latestStatusEntryId || '';
  if (latestStatusEntryId && statusEntry?._id) {
    return String(statusEntry._id) === String(latestStatusEntryId);
  }

  const latestEntry = getLatestStatusEntry(candidate?.Status);
  if (!latestEntry?._id || !statusEntry?._id) return false;
  return String(latestEntry._id) === String(statusEntry._id);
}

export function isRejectedCandidate(candidate) {
  return REJECTED_STATUSES.has(getLatestCandidateStatusRaw(candidate));
}

export const ORNNOVA_SCREEN_REJECT_STATUS = 'ORNNOVA Screening Reject';

export function isOrnnovaScreenRejectCandidate(candidate) {
  const status = getLatestCandidateStatusRaw(candidate);
  if (status === ORNNOVA_SCREEN_REJECT_STATUS) return true;
  return getStatusDisplayLabel(status) === 'Ornnova Screen Reject';
}

export function isActiveProfile(candidate) {
  if (candidate?.savedStatus !== 'Uploaded') return false;
  return !isRejectedCandidate(candidate);
}

export function filterActiveCandidates(candidates = []) {
  return candidates.filter(isActiveProfile);
}

export const getStatusUpdateOptions = () => ALL_STATUS_OPTIONS;

export const getNextStatusOptions = () => ALL_STATUS_OPTIONS;

export const INTERVIEW_SCHEDULE_STATUSES = [...STATUSES_REQUIRING_INTERVIEW_DATE];

export function statusRequiresInterviewDate(status) {
  return STATUSES_REQUIRING_INTERVIEW_DATE.has(status);
}

export function isInterviewScheduleStatus(status) {
  return statusRequiresInterviewDate(status);
}

function parseCandidateActionDate(value) {
  if (value == null || value === '') return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() < 1971) return null;
  return parsed;
}

function startOfCalendarDay(date) {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
}

function getCandidateUploadedOnDate(candidate) {
  return parseCandidateActionDate(candidate?.uploadedOn) || parseCandidateActionDate(candidate?.date);
}

function isValidStatusActionEntry(entry) {
  const status = entry?.Status;
  return Boolean(status && status !== 'No Action Taken');
}

function getLatestStatusActionDate(candidate) {
  const statusList = candidate?.Status || [];
  let latest = null;

  statusList.forEach((entry) => {
    if (!isValidStatusActionEntry(entry)) return;
    const actionDate = parseCandidateActionDate(entry?.Date);
    if (!actionDate) return;
    if (!latest || actionDate > latest) latest = actionDate;
  });

  return latest;
}

function getNoActionReferenceDate(candidate) {
  return getLatestStatusActionDate(candidate) || getCandidateUploadedOnDate(candidate);
}

export function isNoActionTakenCandidate(candidate, now = new Date()) {
  const referenceDate = getNoActionReferenceDate(candidate);
  if (!referenceDate) return false;

  const referenceDay = startOfCalendarDay(referenceDate);
  const today = startOfCalendarDay(now);
  const daysSinceReference = Math.floor((today - referenceDay) / (24 * 60 * 60 * 1000));
  return daysSinceReference >= 2;
}

export function isNoActionTakenProfile(candidate) {
  if (isRejectedCandidate(candidate)) return false;
  return isNoActionTakenCandidate(candidate);
}

export function filterNoActionTakenCandidates(candidates = []) {
  return candidates.filter(isNoActionTakenProfile);
}

export function countNoActionCandidates(candidates = []) {
  return filterNoActionTakenCandidates(candidates).length;
}

export function countActionTakenCandidates(candidates = []) {
  return candidates.filter((candidate) => {
    if (!candidate?.Status?.length) return false;
    const latest = getLatestCandidateStatusRaw(candidate);
    return latest !== 'No Action Taken' && !REJECTED_STATUSES.has(latest);
  }).length;
}

export function countRejectedCandidates(candidates = []) {
  return candidates.filter(isRejectedCandidate).length;
}
